// src/hooks/useCollectionPoints.ts

import { useState, useCallback } from 'react';
import type { CollectionPoint } from '../types';
import {
    fetchCollectionPointsFromDb,
    createCollectionPointInDb,
    updateCollectionPointInDb,
    deleteCollectionPointFromDb,
    type CreateCollectionPointPayload,
    type UpdateCollectionPointPayload,
} from '../services/collectionPointService';
import {getErrorMessage} from "../utils/errors.ts";

export function useCollectionPoints() {
    const [points, setPoints] = useState<CollectionPoint[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchPoints = useCallback(async (): Promise<CollectionPoint[]> => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchCollectionPointsFromDb();
            setPoints(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch collection points'));
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    const createPoint = useCallback(async (
        payload: CreateCollectionPointPayload
    ): Promise<CollectionPoint | null> => {
        setError(null);
        try {
            const created = await createCollectionPointInDb(payload);
            setPoints(prev => [...prev, created]);
            return created;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to create collection point';
            setError(msg);
            return null;
        }
    }, []);

    const updatePoint = useCallback(async (
        id: string,
        patch: UpdateCollectionPointPayload
    ): Promise<CollectionPoint | null> => {
        setError(null);
        try {
            const updated = await updateCollectionPointInDb(id, patch);
            setPoints(prev => prev.map(p => p.id === id ? updated : p));
            return updated;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to update collection point';
            setError(msg);
            return null;
        }
    }, []);

    const toggleActive = useCallback(async (point: CollectionPoint): Promise<boolean> => {
        setError(null);
        try {
            const updated = await updateCollectionPointInDb(point.id, {
                is_active: !point.is_active,
            });
            setPoints(prev => prev.map(p => p.id === point.id ? updated : p));
            return true;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to toggle collection point';
            setError(msg);
            return false;
        }
    }, []);

    const deletePoint = useCallback(async (id: string): Promise<boolean> => {
        setError(null);
        try {
            await deleteCollectionPointFromDb(id);
            setPoints(prev => prev.filter(p => p.id !== id));
            return true;
        } catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to delete collection point';
            setError(msg);
            return false;
        }
    }, []);

    return {
        points,
        loading,
        error,
        fetchPoints,
        createPoint,
        updatePoint,
        toggleActive,
        deletePoint,
    };
}